import { useApolloClient, useQuery } from '@apollo/client/react';
import { notifications } from '@mantine/notifications';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';

import { onSessionExpired } from '../apollo/session';
import { AdminMeDocument } from '../graphql/operations';
import { AuthContext, type AuthContextValue } from './context';
import { clearToken, readToken, writeToken } from './token';

/**
 * Who is signed in, for the whole app.
 *
 * The token is the source of truth (it is what the API checks); `adminMe` is
 * how the console finds out whether the one in storage is still good, and it is
 * run on boot for exactly that reason — a twelve-hour token that expired
 * overnight should land on the login page, not on a dashboard full of errors.
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setToken] = useState<string | null>(() => readToken());
  const client = useApolloClient();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { data, loading } = useQuery(AdminMeDocument, {
    skip: !token,
    // The token can go stale between visits, so the answer is never taken from
    // the cache alone — but the cached copy is shown while it is rechecked.
    fetchPolicy: 'cache-and-network',
  });

  // What the Apollo error link calls when the API answers UNAUTHENTICATED.
  useEffect(
    () =>
      onSessionExpired(() => {
        setToken(null);
        void client.clearStore();
        notifications.show({
          color: 'red',
          title: t('errors.sessionExpired.title'),
          message: t('errors.sessionExpired.message'),
        });
        void navigate('/login', { replace: true });
      }),
    [client, navigate, t],
  );

  const signIn = useCallback((accessToken: string) => {
    writeToken(accessToken);
    setToken(accessToken);
  }, []);

  const signOut = useCallback(() => {
    clearToken();
    setToken(null);
    void client.clearStore();
    void navigate('/login', { replace: true });
  }, [client, navigate]);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      admin: data?.adminMe ?? null,
      // Only the very first load blocks: a background recheck of a token that
      // already produced an admin should not blank the screen.
      hydrating: Boolean(token) && loading && !data,
      signIn,
      signOut,
    }),
    [token, data, loading, signIn, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
